import { Link } from "react-router-dom";
import { useLang, track } from "@/context/LanguageContext";
import { Reveal, ImageReveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/Editorial";
import { PageHead } from "@/components/Shared";
import { usePageMeta } from "@/lib/seo";
import { IMAGES } from "@/data/config";

const BOOK_1TO1 = "https://backoffice.bsport.io/m/Maison%20Core/6040/private-service/30072?tabSelected=2";
const BOOK_DUO = "https://backoffice.bsport.io/m/Maison%20Core/6040/private-service/30162?tabSelected=2";
const PACK_1TO1_5 = "https://backoffice.bsport.io/customer/payment/pass/784101/?membership=6040&force=true";
const PACK_1TO1_10 = "https://backoffice.bsport.io/customer/payment/pass/784102/?membership=6040&force=true";
const PACK_DUO_5 = "https://backoffice.bsport.io/customer/payment/pass/784107/?membership=6040&force=true";
const PACK_DUO_10 = "https://backoffice.bsport.io/customer/payment/pass/784108/?membership=6040&force=true";

const COPY = {
  en: {
    heroLabel: "Private Pilates",
    heroTitle: "A practice, made personal.",
    heroLead: "For those who want focused, one-to-one attention — private Reformer Pilates shaped entirely around you.",
    introLabel: "Why private",
    introBody: "Private sessions at Maison Core are built around your goals, your body and your pace. Whether you're beginning, returning after injury, preparing for something specific, or simply prefer a quieter setting, your instructor designs every session around you — with precision, care and undivided attention.",
    formatsLabel: "Options",
    formatsTitle: "Choose your setting.",
    formats: [
      { name: "Private 1:1", price: "€99", duration: "60 minutes", body: "You and your instructor. The most focused way to progress, with fully personalised programming.", bookCta: "Book Private 1:1", bookUrl: BOOK_1TO1 },
      { name: "Private Duo", price: "€140", duration: "60 minutes", body: "Train with a partner or friend. Shared attention, personal guidance — for two.", bookCta: "Book Private Duo", bookUrl: BOOK_DUO },
    ],
    formatsNote: "Prices are per 60-minute session.",
    packsLabel: "Private packs",
    packsTitle: "Commit to your practice.",
    packsBody: "Prefer to train privately over time? Private packs offer a considered rhythm and a lower price per session.",
    packsGroups: [
      { name: "Private 1:1", items: [{ sessions: "5 sessions", price: "€475", per: "€95 / session", url: PACK_1TO1_5 }, { sessions: "10 sessions", price: "€900", per: "€90 / session", url: PACK_1TO1_10 }] },
      { name: "Private Duo", items: [{ sessions: "5 sessions", price: "€675", per: "€135 / session", url: PACK_DUO_5 }, { sessions: "10 sessions", price: "€1100", per: "€110 / session", url: PACK_DUO_10 }] },
    ],
    packBuyCta: "Buy pack",
    packsNote: "After purchase, book your sessions at a time that suits you.",
    packsCta: "Questions about packs?",
    howLabel: "How it works",
    howTitle: "Your private session, step by step.",
    howSteps: [
      { n: "01", t: "Choose your session", b: "Choose Private 1:1 or Private Duo depending on how you would like to train." },
      { n: "02", t: "Book your time", b: "Continue to our Bsport booking system, choose an available date and time and complete your booking." },
      { n: "03", t: "Complete your intake", b: "After booking, we ask you to complete a short Private Pilates Intake before your first session. It takes only a few minutes and helps your instructor prepare for you." },
      { n: "04", t: "Join us at Maison Core", b: "Come to Maison Core for your 60-minute Private Pilates session. Your instructor will guide you from there." },
    ],
    expectLabel: "What to expect",
    expectItems: [
      { t: "Tailored programming", b: "Every session is designed around your goals, level and movement needs." },
      { t: "Undivided attention", b: "Detailed, hands-on guidance and real-time adjustments throughout." },
      { t: "Your pace, your goals", b: "Ideal for beginners, returning clients, or focused, specific training." },
    ],
    intakeLabel: "Private Pilates intake",
    intakeTitle: "Before we meet.",
    intakeBody: "First-time Private Pilates clients complete a short intake after booking — it only takes a few minutes and helps your instructor prepare for your session.",
    intakeCta: "Complete your intake",
  },
  nl: {
    heroLabel: "Privéles",
    heroTitle: "Een praktijk, persoonlijk gemaakt.",
    heroLead: "Voor wie gerichte, persoonlijke aandacht wil — privé Reformer Pilates volledig om jou heen gebouwd.",
    introLabel: "Waarom privé",
    introBody: "Privésessies bij Maison Core zijn opgebouwd rond jouw doelen, jouw lichaam en jouw tempo. Of je nu begint, terugkeert na een blessure, je voorbereidt op iets specifieks of gewoon een rustigere setting prefereert — je instructeur ontwerpt elke sessie om jou heen, met precisie, zorg en onverdeelde aandacht.",
    formatsLabel: "Opties",
    formatsTitle: "Kies jouw setting.",
    formats: [
      { name: "Private 1:1", price: "€99", duration: "60 minuten", body: "Jij en je instructeur. De meest gerichte manier om te groeien, met een volledig persoonlijk programma.", bookCta: "Boek Private 1:1", bookUrl: BOOK_1TO1 },
      { name: "Private Duo", price: "€140", duration: "60 minuten", body: "Train met een partner of vriend(in). Gedeelde aandacht, persoonlijke begeleiding — voor twee.", bookCta: "Boek Private Duo", bookUrl: BOOK_DUO },
    ],
    formatsNote: "Prijzen zijn per sessie van 60 minuten.",
    packsLabel: "Privépakketten",
    packsTitle: "Kies voor continuïteit.",
    packsBody: "Liever langere tijd privé trainen? Privépakketten geven een vast ritme en een lagere prijs per sessie.",
    packsGroups: [
      { name: "Private 1:1", items: [{ sessions: "5 sessies", price: "€475", per: "€95 / sessie", url: PACK_1TO1_5 }, { sessions: "10 sessies", price: "€900", per: "€90 / sessie", url: PACK_1TO1_10 }] },
      { name: "Private Duo", items: [{ sessions: "5 sessies", price: "€675", per: "€135 / sessie", url: PACK_DUO_5 }, { sessions: "10 sessies", price: "€1100", per: "€110 / sessie", url: PACK_DUO_10 }] },
    ],
    packBuyCta: "Koop pakket",
    packsNote: "Na aankoop boek je je sessies op een moment dat jou uitkomt.",
    packsCta: "Vragen over pakketten?",
    howLabel: "Hoe het werkt",
    howTitle: "Jouw privésessie, stap voor stap.",
    howSteps: [
      { n: "01", t: "Kies je sessie", b: "Kies Private 1:1 of Private Duo, afhankelijk van hoe je wilt trainen." },
      { n: "02", t: "Boek je tijd", b: "Ga verder naar ons Bsport-boekingssysteem, kies een beschikbare datum en tijd en rond je boeking af." },
      { n: "03", t: "Vul je intake in", b: "Na het boeken vragen we je om vóór je eerste sessie een korte Private Pilates Intake in te vullen. Het duurt maar een paar minuten en helpt je instructeur zich op jou voor te bereiden." },
      { n: "04", t: "Kom naar Maison Core", b: "Kom naar Maison Core voor je Private Pilates-sessie van 60 minuten. Je instructeur begeleidt je vanaf daar." },
    ],
    expectLabel: "Wat je kunt verwachten",
    expectItems: [
      { t: "Programma op maat", b: "Elke sessie is ontworpen rond jouw doelen, niveau en bewegingsbehoeften." },
      { t: "Onverdeelde aandacht", b: "Gedetailleerde, hands-on begeleiding en realtime correcties gedurende de hele sessie." },
      { t: "Jouw tempo, jouw doelen", b: "Ideaal voor beginners, terugkerende clients of gericht, specifiek trainen." },
    ],
    intakeLabel: "Private Pilates intake",
    intakeTitle: "Voordat we elkaar zien.",
    intakeBody: "Nieuwe Private Pilates-clients vullen na het boeken een korte intake in — het duurt maar een paar minuten en helpt je instructeur zich voor te bereiden.",
    intakeCta: "Vul je intake in",
  },
};

export default function Private() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;

  usePageMeta(
    lang === "nl"
      ? {
          title: "Privéles — Maison Core Reformer Pilates, Amsterdam",
          description: "Privé Reformer Pilates bij Maison Core Amsterdam. Private 1:1 (€99) of Duo (€140), 60 minuten, met onverdeelde aandacht en een programma op maat.",
          canonical: "https://maisoncore.nl/private",
        }
      : {
          title: "Private Pilates — Maison Core Reformer, Amsterdam",
          description: "Private Reformer Pilates at Maison Core Amsterdam. Private 1:1 (€99) or Duo (€140), 60 minutes, with undivided attention and a programme tailored to you.",
          canonical: "https://maisoncore.nl/private",
        }
  );

  return (
    <>
      <PageHead label={c.heroLabel} title={c.heroTitle} lead={c.heroLead} />

      {/* INTRO + IMAGE */}
      <section className="bg-petal" data-testid="private-intro">
        <div className="mx-auto grid max-w-[1600px] items-stretch gap-0 md:grid-cols-2">
          <ImageReveal src={IMAGES.equipCarriage} alt="Private Reformer Pilates session at Maison Core Amsterdam" className="aspect-[4/5] md:aspect-auto md:h-full md:min-h-[560px]" />
          <div className="px-6 py-16 md:px-16 md:py-28">
            <SectionLabel>{c.introLabel}</SectionLabel>
            <Reveal>
              <p className="mt-8 font-serif text-3xl leading-snug text-onyx md:text-4xl">{c.introBody}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OPTIONS — direct Bsport booking */}
      <section className="bg-onyx text-petal" data-testid="private-formats">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
          <SectionLabel light>{c.formatsLabel}</SectionLabel>
          <h2 className="mt-8 font-serif text-5xl md:text-7xl">{c.formatsTitle}</h2>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {c.formats.map((f, i) => (
              <Reveal key={f.name} delay={i * 0.08}>
                <div className="flex h-full flex-col justify-between gap-10 border border-petal/20 p-8 md:p-10" data-testid={`private-format-${i}`}>
                  <div>
                    <div className="flex items-baseline justify-between gap-4 border-b border-petal/15 pb-5">
                      <h3 className="font-serif text-4xl">{f.name}</h3>
                      <span className="font-serif text-3xl text-blush">{f.price}</span>
                    </div>
                    <p className="label mt-4 text-petal/55">{f.duration}</p>
                    <p className="mt-4 font-sans text-base leading-relaxed text-petal/75">{f.body}</p>
                  </div>
                  <a
                    href={f.bookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track("private_book_click", { format: f.name, source: "private" })}
                    className="label border border-petal py-4 text-center transition-colors duration-500 hover:bg-petal hover:text-onyx"
                    data-testid={`private-book-${i}`}
                  >
                    {f.bookCta}
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 font-sans text-sm text-petal/55">{c.formatsNote}</p>
        </div>
      </section>

      {/* PRIVATE PACKS */}
      <section className="bg-petal" data-testid="private-packs">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
          <SectionLabel>{c.packsLabel}</SectionLabel>
          <h2 className="mt-8 font-serif text-5xl md:text-6xl">{c.packsTitle}</h2>
          <p className="mt-6 max-w-xl font-sans text-lg text-onyx/70">{c.packsBody}</p>
          <div className="mt-14 grid gap-12 md:grid-cols-2">
            {c.packsGroups.map((grp) => (
              <div key={grp.name} className="border-t border-onyx/20 pt-8" data-testid={`private-packgroup-${grp.name}`}>
                <h3 className="font-serif text-3xl">{grp.name}</h3>
                <div className="mt-6 space-y-4">
                  {grp.items.map((it, idx) => (
                    <div key={it.sessions} className="border-b border-onyx/10 pb-5">
                      <div className="flex flex-wrap items-baseline justify-between gap-3">
                        <span className="font-sans text-lg text-onyx/80">{it.sessions}</span>
                        <span className="flex items-baseline gap-3">
                          <span className="font-serif text-3xl">{it.price}</span>
                          <span className="label text-clay">{it.per}</span>
                        </span>
                      </div>
                      <a
                        href={it.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track("private_pack_buy_click", { pack: `${grp.name} ${it.sessions}`, source: "private" })}
                        className="label mt-4 inline-block border border-onyx px-6 py-3 transition-colors duration-500 hover:bg-onyx hover:text-petal"
                        data-testid={`private-pack-buy-${grp.name.replace(/[^a-z0-9]/gi, "")}-${idx}`}
                      >
                        {c.packBuyCta}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <p className="font-sans text-sm text-onyx/60">{c.packsNote}</p>
            <Link to="/contact?topic=private" onClick={() => track("private_pack_enquiry", { source: "private" })} className="label border-b border-onyx pb-1 hover:opacity-60" data-testid="private-packs-cta">{c.packsCta} →</Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-petal" data-testid="private-how">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <SectionLabel>{c.howLabel}</SectionLabel>
              <h2 className="mt-8 font-serif text-5xl leading-[0.98] md:text-6xl">{c.howTitle}</h2>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              {c.howSteps.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.06}>
                  <div className="flex gap-6 border-b border-onyx/15 py-8 md:gap-10" data-testid={`private-step-${s.n}`}>
                    <span className="font-serif text-3xl text-terracotta md:text-4xl">{s.n}</span>
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl">{s.t}</h3>
                      <p className="mt-3 max-w-xl font-sans text-base leading-relaxed text-onyx/70">{s.b}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT TO EXPECT */}
      <section className="bg-blush/40" data-testid="private-expect">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-24">
          <SectionLabel>{c.expectLabel}</SectionLabel>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {c.expectItems.map((it, i) => (
              <Reveal key={it.t} delay={i * 0.08}>
                <div className="border-t border-onyx/20 pt-6" data-testid={`private-expect-${i}`}>
                  <p className="label text-terracotta">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-serif text-3xl leading-snug">{it.t}</h3>
                  <p className="mt-3 font-sans text-base leading-relaxed text-onyx/70">{it.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INTAKE (after booking) */}
      <section className="bg-rust text-petal" data-testid="private-intake-cta">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-8 px-6 py-20 md:flex-row md:items-end md:justify-between md:px-12 md:py-28">
          <div className="max-w-2xl">
            <SectionLabel light>{c.intakeLabel}</SectionLabel>
            <h2 className="mt-6 font-serif text-5xl leading-[0.98] md:text-7xl">{c.intakeTitle}</h2>
            <p className="mt-6 font-sans text-lg leading-relaxed text-petal/80">{c.intakeBody}</p>
          </div>
          <Link
            to="/private-intake"
            onClick={() => track("private_intake_click", { source: "private" })}
            className="label shrink-0 border border-petal bg-petal px-10 py-5 text-onyx transition-colors duration-500 hover:bg-transparent hover:text-petal"
            data-testid="private-intake-cta-btn"
          >
            {c.intakeCta}
          </Link>
        </div>
      </section>
    </>
  );
}
